import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNetworkStatus } from 'react-adaptive-hooks';
import './media-item.scss';

const Video = ({ video, poster }) => {
    const {
        largeUrl: videoLarge,
        mediumUrl: videoMedium,
        smallUrl: videoSmall
    } = video;

    const [loops, setLoops] = useState(0);

    const videoRef = useRef(null);

    const handleVideoEnded = useCallback(() => {
        const video = videoRef.current;
        let videoLoops;

        // Set number of loops depending on video length
        if (video.duration > 30) {
            videoLoops = 2;
        } else if (video.duration > 10) {
            videoLoops = 4;
        } else {
            videoLoops = 8;
        }

        if (loops < videoLoops) {
            video.play();
        }
        setLoops(loops + 1);
    }, [loops]);

    useEffect(() => {
        const videoElement = videoRef.current;
        videoElement.addEventListener('ended', handleVideoEnded);
        return () => {
            videoElement.removeEventListener('ended', handleVideoEnded);
        };
    });

    return (
        <div className="media-item--video">
            <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={true}
                poster={poster}>
                <source
                    type="video/mp4"
                    src={videoLarge}
                    media="all and (min-width:1080px)"
                />
                <source
                    type="video/mp4"
                    src={videoMedium}
                    media="all and (min-width:768px)"
                />
                <source type="video/mp4" src={videoSmall} />
            </video>
        </div>
    );
};

const MediaItem = ({
    alternativeText,
    heading,
    imageSizes,
    image,
    video,
    webpImage
}) => {
    const { effectiveConnectionType } = useNetworkStatus();

    if (!image) {
        return null;
    }

    const { srcSet, sizes } = imageSizes;

    const { largeUrl, mediumUrl, smallUrl } = image;

    //Return image instead of video if on slow connection
    let media;
    switch (effectiveConnectionType) {
        case 'slow-2g':
        case '2g':
        case '3g':
            media = (
                <picture>
                    {webpImage && webpImage}
                    <img
                        srcSet={srcSet}
                        sizes={sizes}
                        src={smallUrl}
                        alt={alternativeText || heading}
                    />
                </picture>
            );
            break;
        default:
            media = video ? (
                <Video video={video} poster={mediumUrl} />
            ) : (
                <picture>
                    {webpImage && webpImage}
                    <img
                        srcSet={srcSet}
                        sizes={sizes}
                        src={largeUrl}
                        alt={alternativeText || heading}
                    />
                </picture>
            );
            break;
    }

    return <div className="media-item">{media}</div>;
};

MediaItem.propTypes = {
    alternativeText: PropTypes.string,
    heading: PropTypes.string,
    imageSizes: PropTypes.shape({
        srcSet: PropTypes.string,
        sizes: PropTypes.string
    }),
    image: PropTypes.object.isRequired,
    video: PropTypes.object
};

export default MediaItem;
